import { useSelector } from 'react-redux';
import { selectIsBossOrSalesBossLevel } from 'core/userContext';
import { DISPLAY_CONTENT } from 'modules/globalSms/constants';
import { EditablePreferenceFields, Frequency } from 'modules/globalSms/types';
import { useController } from 'react-hook-form';
import { Lock } from 'ui/icons';
import { Radio } from 'ui/radio';
import { BossBadge } from 'ui/badge';
import {
  DisabledTooltip,
  FieldTitle,
  FrequencyDisclaimer,
  RadioFormContent,
  RadioOption,
  StyledFormField,
} from './styles';

const {
  frequencyDisclaimer,
  frequencyDisclaimerNonUSOrCanadaAndDisabled,
  frequencyFieldTitle,
  frequencyRadioLabelEvery,
  frequencyRadioLabelFirst,
  frequencyRadioLabelNever,
  frequencyTooltipContent,
} = DISPLAY_CONTENT.editModal;

type Props = { isDisabled: boolean };

export const FrequencyFormField = ({ isDisabled, ...props }: Props) => {
  const { field, formState } = useController<EditablePreferenceFields>({ name: 'frequency' });
  const isBossUser = useSelector(selectIsBossOrSalesBossLevel);
  const shouldShowNeverOption =
    isBossUser || formState.defaultValues?.frequency === Frequency.DISABLED;

  const handleChange = (type: Frequency) => field.onChange(type);

  const formFieldLabelText = (
    <DisabledTooltip tooltipContent={frequencyTooltipContent} isEnabled={isDisabled}>
      <FieldTitle>
        {isDisabled && <Lock height={22} width={20} />}
        {frequencyFieldTitle}
      </FieldTitle>
    </DisabledTooltip>
  );

  return (
    <StyledFormField
      data-test="edit-preferences-modal-form-field-frequency"
      isDisabled={isDisabled}
      labelText={formFieldLabelText}
      {...props}
    >
      <RadioFormContent>
        <RadioOption>
          <Radio
            data-test="radio-frequency-every"
            id="frequency-every"
            isChecked={field.value === Frequency.EVERY}
            isDisabled={isDisabled}
            onClick={() => handleChange(Frequency.EVERY)}
            value={field.value}
          />
          {frequencyRadioLabelEvery}
        </RadioOption>
        <RadioOption>
          <Radio
            data-test="radio-frequency-first"
            id="frequency-first"
            isChecked={field.value === Frequency.FIRST}
            isDisabled={isDisabled}
            onClick={() => handleChange(Frequency.FIRST)}
            value={field.value}
          />
          {frequencyRadioLabelFirst}
        </RadioOption>
        {shouldShowNeverOption && (
          <RadioOption>
            <Radio
              data-test="radio-frequency-disabled"
              id="frequency-disabled"
              isChecked={field.value === Frequency.DISABLED}
              isDisabled={isDisabled}
              onClick={() => handleChange(Frequency.DISABLED)}
              value={field.value}
            />
            {frequencyRadioLabelNever}
            {isBossUser && <BossBadge />}
          </RadioOption>
        )}
        {!shouldShowNeverOption && <FrequencyDisclaimer>{frequencyDisclaimer}</FrequencyDisclaimer>}
        {shouldShowNeverOption && isDisabled && (
          <FrequencyDisclaimer>{frequencyDisclaimerNonUSOrCanadaAndDisabled}</FrequencyDisclaimer>
        )}
      </RadioFormContent>
    </StyledFormField>
  );
};
